import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { CheckCircle2, MessageCircle, MapPin, Home, Building2, PackageCheck, Sparkles, Truck } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { StickyCTA } from "@/components/StickyCTA";
import { Seo } from "@/components/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CtaBanner } from "@/components/CtaBanner";
import { MediaPanel } from "@/components/MediaPanel";
import { getService, areas, SITE_URL } from "@/data/seo";
import { waLink } from "@/lib/site";

const ICONS = {
  "house-removals": Home,
  "office-removals": Building2,
  "packing": PackageCheck,
  "end-of-tenancy-cleaning": Sparkles,
  "man-and-van": Truck,
};

export const ServicePage = () => {
  const { serviceSlug } = useParams();
  const svc = getService(serviceSlug);
  if (!svc) return <Navigate to="/services" replace />;

  const canonical = `/services/${svc.slug}`;
  const jsonld = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: svc.name,
    description: svc.metaDescription,
    serviceType: svc.name,
    provider: {
      "@type": "MovingCompany",
      name: "BoxB Solutions",
      telephone: "+447570291363",
      email: "info@boxb.uk",
      url: SITE_URL,
      address: { "@type": "PostalAddress", addressLocality: "Cardiff", addressRegion: "South Wales", addressCountry: "GB" },
    },
    areaServed: Object.values(areas).map((a) => a.name),
    aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "127" },
    offers: { "@type": "Offer", availability: "https://schema.org/InStock", priceCurrency: "GBP" },
    url: `${SITE_URL}${canonical}`,
  };

  return (
    <main className="bg-white min-h-screen">
      <Seo title={svc.metaTitle} description={svc.metaDescription} canonical={canonical} jsonld={jsonld} />
      <Nav />

      <section className="pt-28 md:pt-32 pb-12">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Breadcrumbs trail={[
            { href: "/", label: "Home" },
            { href: "/services", label: "Services" },
            { href: canonical, label: svc.shortName },
          ]} />

          <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <span className="text-xs tracking-[0.22em] uppercase font-bold text-[#2563EB]">{svc.tag}</span>
              <h1 className="mt-4 font-display font-bold text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
                {svc.name} in Cardiff &amp; South Wales
              </h1>
              <p className="mt-6 text-lg text-slate-600 leading-relaxed">{svc.lead}</p>

              <ul className="mt-8 space-y-3">
                {svc.highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-[#2563EB] mt-0.5 shrink-0" />
                    <span className="text-slate-700">{h}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-9 flex flex-wrap gap-3">
                <a href={waLink(`Hi BoxB, I'd like a ${svc.shortName.toLowerCase()} quote please.`)}
                   target="_blank" rel="noopener noreferrer" data-testid="service-wa-btn"
                   className="inline-flex items-center gap-2 rounded-full bg-[#25D366] text-white px-7 py-4 font-semibold hover:brightness-95">
                  <MessageCircle className="h-4 w-4" /> WhatsApp for a quote
                </a>
                <Link to="/#contact" data-testid="service-quote-btn"
                      className="inline-flex items-center gap-2 rounded-full bg-[#0A192F] text-white px-7 py-4 font-semibold hover:bg-[#0F172A]">
                  Get free quote
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5">
              {svc.image ? (
                <div className="relative overflow-hidden rounded-[2rem] border border-slate-200 shadow-[0_20px_60px_rgb(10,25,47,0.08)] h-[420px]">
                  <img src={svc.image} alt={`${svc.name} in Cardiff and South Wales by BoxB Solutions`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/40 via-transparent to-transparent" />
                </div>
              ) : (
                <MediaPanel
                  variant={svc.slug}
                  icon={ICONS[svc.slug]}
                  label={svc.shortName}
                  height="h-[420px]"
                  className="shadow-[0_20px_60px_rgb(10,25,47,0.08)]"
                />
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display font-semibold text-3xl text-[#0A192F] tracking-tight">Frequently asked questions</h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            {svc.faqs.map((f) => (
              <div key={f.q} className="rounded-3xl bg-white border border-slate-200 p-7">
                <h3 className="font-display font-semibold text-lg text-[#0A192F]">{f.q}</h3>
                <p className="mt-2 text-slate-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <h2 className="font-display font-semibold text-3xl text-[#0A192F] tracking-tight">
            {svc.shortName} across South Wales
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl">Choose your location for a local page with pricing and coverage details.</p>
          <div className="mt-8 grid sm:grid-cols-2 md:grid-cols-4 gap-3">
            {Object.values(areas).map((a) => (
              <Link key={a.slug} to={`/services/${svc.slug}/${a.slug}`}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-[#0A192F] transition-colors">
                <MapPin className="h-4 w-4 text-[#2563EB]" />
                <span className="font-medium text-[#0A192F]">{svc.shortName} {a.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaBanner />
      <Footer />
      <StickyCTA />
    </main>
  );
};
